export interface AttachmentResponse {
	url?: string;
	data?: string;
	success: boolean;
	statusCode?: number;
	message?: string;
}

/**
 * Fetches attachment URL by key
 * @param key - The attachment key from backend
 * @returns Promise<AttachmentResponse> - Object containing the URL and success status
 */
export const getAttachmentUrl = async () => {
	try {
		// TODO: Add proper attachment URL endpoint
		// const response = await authApi.get(SERVER_ROUTES.INSTITUTES.GET_ATTACHMENT_URL(key));
		// return response.data;
		return {
			url: "",
			success: false,
			message: "Attachment URL endpoint not implemented",
		};
	} catch (error) {
		console.error("Error fetching attachment URL:", error);
		return {
			url: "",
			success: false,
			message: "Failed to fetch attachment URL",
		};
	}
};

/**
 * Utility function to get attachment URL and handle errors
 * @param key - The attachment key
 * @returns Promise<string> - The attachment URL or empty string if failed
 */
export const getAttachmentUrlString = async () => {
	const result = await getAttachmentUrl();
	console.log("resultresultresult", result);
	return result;
};

/**
 * Utility function to check if attachment key is valid
 * @param key - The attachment key to validate
 * @returns boolean - True if key is valid (not empty, null, or undefined)
 */
export const isValidAttachmentKey = (key: string | null | undefined): boolean => {
	return Boolean(key && key.trim() !== "");
};

/**
 * Converts a file path to use the new attachment API endpoint
 * @param filePath - The file path from the backend (e.g., "uploads/file.jpg")
 * @returns string - The attachment key to use with the API
 */
export const convertFilePathToAttachmentKey = (filePath: string): string => {
	// Remove any leading slashes and return the path as the key
	return filePath.replace(/^\/+/, "");
};

/**
 * Gets the attachment URL for a file path using the new API
 * @param filePath - The file path from the backend
 * @returns Promise<string> - The actual file URL or empty string if failed
 */
export const getAttachmentUrlFromFilePath = async (filePath: string) => {
	if (!filePath) return "";

	return await getAttachmentUrlString();
};



export const getFullFileUrl = (filePath: string) => {
	if (!filePath) return "";

	// If the filePath is already a full URL (including blob URLs), return it as is
	if (filePath.startsWith("http://") || filePath.startsWith("https://") || filePath.startsWith("blob:")) {
		return filePath;
	}

	// For regular file paths, return as is (they might need to be processed by the attachment API)
	return filePath;
};

const normalizeAttachmentUrl = (url: string): string => {
	const trimmed = url.trim();
	if (!trimmed) return "";
	if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:")) {
		return trimmed;
	}
	return `https://${trimmed}`;
};

const getFilenameFromPath = (filePathOrName: string): string => {
	const trimmed = filePathOrName.trim();
	if (!trimmed) return "";
	return trimmed.includes("/") ? trimmed.split("/").pop() || trimmed : trimmed;
};

/**
 * Opens an attachment in a new tab. Opens the tab synchronously on click so Chrome
 * does not block the popup after the async presigned-URL fetch completes.
 */
export const openAttachmentInNewTabByFilename = async (filenameOrPath: string): Promise<boolean> => {
	const filename = getFilenameFromPath(filenameOrPath);
	if (!filename) {
		alert("Filename is required");
		return false;
	}

	// Must open synchronously during the user click — do not pass "noopener" (returns null).
	const previewWindow = window.open("", "_blank");
	if (!previewWindow) {
		alert("Please allow pop-ups for this site to view attachments.");
		return false;
	}

	previewWindow.document.title = "Loading attachment...";
	previewWindow.document.body.innerHTML =
		'<p style="font-family:sans-serif;padding:24px;margin:0">Loading attachment...</p>';

	try {
		const result = await getAttachmentUrlByFilename(filename);
		const fileUrlString = result.url || result.data || "";

		if (result.success && fileUrlString.trim() !== "") {
			previewWindow.opener = null;
			previewWindow.location.href = normalizeAttachmentUrl(fileUrlString);
			return true;
		}

		previewWindow.close();
		alert(`Failed to load certificate: ${result.message || "URL not found in response"}`);
		return false;
	} catch (error) {
		console.error("Error opening attachment:", error);
		previewWindow.close();
		alert("Error loading certificate preview. Please try again.");
		return false;
	}
};

/**
 * Fetches attachment URL by filename
 * @param filename - The filename to fetch
 * @returns Promise<AttachmentResponse> - Object containing the URL and success status
 */
export const getAttachmentUrlByFilename = async (filename: string): Promise<AttachmentResponse> => {
	try {
		if (!filename) {
			return {
				url: "",
				success: false,
				message: "Filename is required",
			};
		}

		const { api } = await import("@config/axios/axios.config");
		const { SERVER_ROUTES } = await import("@constants/api.routes");
		
		const response = await api.get<AttachmentResponse | string>(
			SERVER_ROUTES.INSTITUTES.GET_ATTACHMENT(filename)
		);
		
		// Handle case where API returns URL string directly or as an object
		let url = "";
		let success = false;
		let statusCode: number | undefined;
		let message = "Request successful";
		
		// Check if response.data is a string (direct URL) or an object
		if (typeof response.data === "string") {
			// API returned URL string directly
			url = response.data;
			success = Boolean(url && url.trim() !== "");
		} else if (typeof response.data === "object" && response.data !== null) {
			// API returned an object
			const responseData = response.data as AttachmentResponse;
			// Try multiple possible fields where the URL might be
			url = responseData.data || 
			      responseData.url || 
			      (responseData as unknown as { url?: string; data?: string; fileUrl?: string; attachmentUrl?: string }).fileUrl ||
			      (responseData as unknown as { url?: string; data?: string; fileUrl?: string; attachmentUrl?: string }).attachmentUrl ||
			      "";
			success = responseData.success === true && Boolean(url && url.trim() !== "");
			statusCode = responseData.statusCode;
			message = responseData.message || "Request successful";
		}
		
		// Return the URL in both 'url' and 'data' fields for compatibility
		const result = {
			url: url,
			data: url,
			success: success,
			statusCode: statusCode,
			message: message,
		};
		
		return result;
	} catch (error) {
		console.error("Error fetching attachment URL by filename:", error);
		return {
			url: "",
			success: false,
			message: "Failed to fetch attachment URL",
		};
	}
};